import React, { useEffect, useMemo, useState } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import { get, post } from "../utils/api";
import { SquarePen, Search, Filter, Download, Plus, Check, Trash2 } from "lucide-react";
import Button from "../components/Button/Button";
import CustomDialog from "../components/dialog/CustomDialog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import Select from "react-select";

const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editFund, setEditFund] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [mappingData, setMappingData] = useState({
    products: [],
    productTypes: [],
  });
  const [activeFunds, setActiveFunds] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [classGridData, setClassGridData] = useState([]);
  const [selectedFundProductId, setSelectedFundProductId] = useState("");
  const [selectedFundId, setSelectedFundId] = useState("");
  const [selectedProductTypeId, setSelectedProductTypeId] = useState("");
  const [isFundLocked, setIsFundLocked] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    product_id: "",
    product_type_id: "",
    fund_id: "",
    fund_code: "",
    class_code: "",
    placement_fee_per: "",
    upfront_fee_per: "",
    trail_fee_per: "",
    management_fee_sharing_per: "",
    side_letter_management_fee_per: "",
    performance_fee_sharing_per: "",
    is_active: true,
    hide_from_all_distributors: false,
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      id: "",
      product_id: "",
      product_type_id: "",
      fund_id: "",
      fund_code: "",
      class_code: "",
      placement_fee_per: "",
      upfront_fee_per: "",
      trail_fee_per: "",
      management_fee_sharing_per: "",
      side_letter_management_fee_per: "",
      performance_fee_sharing_per: "",
      is_active: true,
      hide_from_all_distributors: false,
    });
    setEditFund(null);
    setFieldErrors({});
    setClassGridData([]);
    setIsFundLocked(false);
  };

  // Check if mandatory fields are filled
  const areMandatoryFieldsFilled = () => {
    return formData.fund_id && formData.fund_code && formData.product_type_id;
  };

  const isValidPercentage = (value) => {
    if (value === null || value === undefined) return true;
    const str = String(value).trim();
    if (str === "") return true;
    const num = parseFloat(str);
    return !isNaN(num) && num >= 0 && num <= 100;
  };

  const isClassCodeUniqueForSave = () => {
    const classUpper = (formData.class_code || "").trim().toUpperCase();
    if (!classUpper || !formData.fund_id) return true;
    return !products.some(
      (p) =>
        String(p.fund_code) === String(formData.fund_code) &&
        (p.class_code || "").trim().toUpperCase() === classUpper &&
        p.id !== formData.id
    );
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const res = await get("/product/fetchProductsNew");
      if (res.data.Success && res.data.Data?.length) {
        setProducts(
          res.data.Data.map((r) => ({
            id: r.id,
            product: r.product_code,
            product_type: r.product_type,
            fund_name: r.fund_name || r.fund_code,
            fund_code: r.fund_code,
            class_code: r.class_code,
            management_fee_sharing: r.management_fee,
            side_letter_management_fee: r.sideletter_fee ?? null,
            performing_fee_sharing: r.performance_fee,
            is_active: r.is_active,
            hide_from_all_distributors: r.distributor_hide ?? false,
            product_id: r.product_id,
            size: r.size ?? null
          }))
        );

      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async () => {
    try {
      const res = await get("/product/fetchProductTypeMappingDetails");
      const d = res.data?.Data?.[0];
      if (d) {
        setMappingData({
          products: d.product_mst_details || [],
          productTypes: d.product_type_mst_details || [],
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchActiveFunds = async () => {
    try {
      const res = await get("/product/fetchFundList");
      if (res.data.Success && res.data.Data) {
        setActiveFunds(res.data.Data);
      } else {
        setActiveFunds([]);
      }
    } catch (err) {
      console.error(err);
      setActiveFunds([]);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchMappings();
    fetchActiveFunds();
    setIsFundLocked(false);
  }, []);

  // Fetch fund details by fund name and populate fund_code & product_type_id
  const fetchFundDetails = async (fundName) => {
    if (!fundName) {
      setFormData((prev) => ({ ...prev, fund_code: "", product_type_id: "" }));
      setSelectedFundProductId(""); // 🆕 reset
      return;
    }

    try {
      const res = await post("/product/fetchFundDetails", { fund_name: fundName });
      const d = res.data?.Data?.[0];
      if (d) {
        const productTypeName = (d.product_type_name || "").trim();
        let matchedProductTypeId = "";
        if (productTypeName) {
          const match = mappingData.productTypes.find(
            (pt) =>
              String(pt.product_type_code || "").trim().toUpperCase() ===
              productTypeName.toUpperCase()
          );
          if (match) matchedProductTypeId = String(match.id);
        }

        // 🆕 Save product_id from API response if available
        if (d.product_id) {
          setSelectedFundProductId(String(d.product_id));
        } else {
          setSelectedFundProductId(""); // reset if missing
        }

        if (d.id) {
          setSelectedFundId(d.id)
        } else {
          setSelectedFundId("")
        }

        if (d.product_type_id) {
          setSelectedProductTypeId(String(d.product_type_id));
        } else {
          setSelectedProductTypeId("");
        }

        setFormData((prev) => ({
          ...prev,
          fund_code: d.fund_code || "",
          product_type_id: matchedProductTypeId,
        }));
        return;
      }
    } catch (err) {
      console.error("fetchFundDetails error:", err);
    }

    // Fallback to activeFunds
    const fund = activeFunds.find((x) => String(x.fund_name) === String(fundName));
    if (fund) {
      let matchedProductTypeId = fund.product_type_id ? String(fund.product_type_id) : "";
      if (!matchedProductTypeId && fund.product_type_name) {
        const productTypeName = String(fund.product_type_name).trim();
        const match = mappingData.productTypes.find(
          (pt) =>
            String(pt.product_type_code || "").trim().toUpperCase() ===
            productTypeName.toUpperCase()
        );
        if (match) matchedProductTypeId = String(match.id);
      }

      // 🆕 Save product_id from /fetchFundList fund object
      if (fund.product_id) {
        setSelectedFundProductId(String(fund.product_id));
      } else {
        setSelectedFundProductId("");
      }

      setFormData((prev) => ({
        ...prev,
        fund_code: fund.fund_code || "",
        product_type_id: matchedProductTypeId,
      }));
    } else {
      setFormData((prev) => ({ ...prev, fund_code: "", product_type_id: "" }));
      setSelectedFundProductId(""); // 🆕 reset
    }
  };

  useEffect(() => {
    fetchFundDetails(formData.fund_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fund_id]);

  useEffect(() => {
    if (classGridData.length === 0) {
      setIsFundLocked(false);
    }
  }, [classGridData]);

  const selectedProductCode = mappingData.products.find(
    (p) => String(p.id) === String(formData.product_id)
  )?.product_code;

  const filteredTypes = useMemo(() => {
    const productCode = mappingData.products.find(
      (p) => String(p.id) === String(formData.product_id)
    )?.product_code;

    let list = [];

    if (productCode === "AIF") {
      list = mappingData.productTypes.filter((pt) =>
        ["CAT II", "CAT III"].includes(pt.product_type_code.toUpperCase())
      );
    } else if (productCode === "PMS") {
      list = mappingData.productTypes.filter((pt) =>
        ["DISCRETIONARY", "NON-DISCRETIONARY", "ADVISORY"].includes(
          pt.product_type_code.toUpperCase()
        )
      );
    } else {
      list = mappingData.productTypes;
    }

    if (
      formData.product_type_id &&
      !list.some((pt) => String(pt.id) === String(formData.product_type_id))
    ) {
      const match = mappingData.productTypes.find(
        (pt) => String(pt.id) === String(formData.product_type_id)
      );
      if (match) list = [...list, match];
    }

    return list;
  }, [mappingData, formData.product_id, formData.product_type_id]);

  const handleOpenDialog = async (row = null) => {
    if (row) {
      try {
        const res = await post("/product/fetchProductsById", {
          product_id: row.id,
          fund_name: row.fund_name,
          class_code: row.class_code,
          product_type: row.product_type,
        });
        const responseData = res.data?.Data;

        if (Array.isArray(responseData) && responseData.length > 0) {
          const data = responseData[0]; // Use first record for common form fields

          setFormData({
            id: data.id,
            product_id: String(data.product_id),
            product_type_id: data.product_type_id ? String(data.product_type_id) : "",
            fund_id: data.fund_name ? String(data.fund_name) : String(data.fund_id || ""),
            fund_code: data.fund_code || "",
            class_code: data.class_code || "",
            placement_fee_per: data.placement_fee_per ?? "",
            upfront_fee_per: data.upfront_fee_per ?? "",
            trail_fee_per: data.trail_fee_per ?? "",
            management_fee_sharing_per: data.management_fee ?? "",
            side_letter_management_fee_per: data.sideletter_fee ?? "",
            performance_fee_sharing_per: data.performance_fee ?? "",
            is_active: data.is_active,
            hide_from_all_distributors: data.distributor_hide ?? false,
          });
          setEditFund(row);

          // Populate Grid from the array
          setClassGridData(
            responseData.map((item) => ({
              class_code: item.class_code,
              management_fee: item.management_fee ?? "",
              side_letter_management_fee: item.sideletter_fee ?? "",
              performance_fee: item.performance_fee ?? "",
              size: item.size ?? "",
            }))
          );
          setDialogVisible(true);
        } else {
          toast.error("No record found.");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error loading item for edit.");
      }
    } else {
      resetForm();
      setDialogVisible(true);
    }
  };

  const handleAddClassCode = () => {
    // Check if mandatory fields are filled
    if (!areMandatoryFieldsFilled()) {
      toast.error("Please fill Fund Name, Fund Code, and Product Type before adding Class Code.");
      return;
    }

    if (!formData.class_code.trim()) {
      toast.error("Please enter Class Code before adding.");
      return;
    }

    if (
      classGridData.some(
        (r) =>
          r.class_code.toUpperCase() === formData.class_code.trim().toUpperCase()
      )
    ) {
      toast.warning("Class Code already exists in grid.");
      return;
    }

    const newRow = {
      class_code: formData.class_code.trim().toUpperCase(),
      management_fee: "",
      side_letter_management_fee: "",
      performance_fee: "",
      size: "",
    };

    setClassGridData((prev) => [...prev, newRow]);

    setIsFundLocked(true);

    setFormData({ ...formData });
  };

  const handleGridChange = (index, field, value) => {
    const updated = [...classGridData];
    updated[index] = { ...updated[index], [field]: value };
    setClassGridData(updated);
  };

  const handleDeleteRow = (index) => {
    setClassGridData((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setIsFundLocked(false); // 🧊 unlock when empty
      return updated;
    });
  };

  const handleClearGrid = () => {
    setClassGridData([]);
    setIsFundLocked(false); // 🧊 unlock when cleared
  };

  const handleSubmit = async () => {
    const {
      product_type_id,
      fund_code,
      class_code,
      placement_fee_per,
      upfront_fee_per,
      trail_fee_per,
      management_fee_sharing_per,
      side_letter_management_fee_per,
      performance_fee_sharing_per,
      hide_from_all_distributors,
    } = formData;

    const selectedFundName = (formData.fund_id || "").toString().trim();
    const normalizedFund = selectedFundName.toUpperCase();

    // --- Duplicate Checks ---
    if (class_code?.trim()) {
      const mainDuplicate = products.some(
        (p) =>
          String(p.fund_name).trim().toUpperCase() === normalizedFund &&
          (p.class_code || "").trim().toUpperCase() === class_code.trim().toUpperCase() &&
          String(p.id) !== String(formData.id)
      );
      if (mainDuplicate) {
        toast.error(
          `Duplicate: Fund "${selectedFundName}" already has Class Code "${class_code
            .trim()
            .toUpperCase()}".`
        );
        return;
      }
    }

    const relevantProducts = products.filter(
      (p) => String(p.fund_name).trim().toUpperCase() === normalizedFund
    );

    for (const row of classGridData) {
      const rowClass = (row.class_code || "").trim().toUpperCase();
      if (!rowClass) continue;

      const exists = relevantProducts.some(
        (p) =>
          (p.class_code || "").trim().toUpperCase() === rowClass &&
          String(p.id) !== String(formData.id)
      );

      if (exists) {
        toast.error(
          `Duplicate: Fund "${selectedFundName}" already has Class Code "${rowClass}".`
        );
        return;
      }
    }

    // --- Validation ---
    const errors = {};
    const trimmedClassCode = (class_code || "").trim();

    if (!product_type_id) errors.product_type_id = "This field is mandatory.";
    if (!fund_code) errors.fund_code = "This field is mandatory.";

    const selectedProductType = filteredTypes.find(
      (pt) => String(pt.id) === formData.product_type_id
    )?.product_type_code?.toUpperCase();

    // 🟢 Validation: All product types use the classGridData
    if (classGridData.length === 0) {
      toast.error("Please add at least one Class row.");
      return;
    }

    for (let i = 0; i < classGridData.length; i++) {
      const row = classGridData[i];
      const rowClass = (row.class_code || "").trim().toUpperCase();

      if (!rowClass) {
        toast.error(`Row ${i + 1}: Class Code is required.`);
        return;
      }

      if (
        row.management_fee === "" ||
        row.management_fee === null ||
        row.management_fee === undefined
      ) {
        toast.error(`Row ${i + 1}: Management Fee is required.`);
        return;
      }

      if (!isValidPercentage(row.management_fee)) {
        toast.error(`Row ${i + 1}: Invalid Management Fee % (0–100).`);
        return;
      }

      if (
        row.performance_fee === "" ||
        row.performance_fee === null ||
        row.performance_fee === undefined
      ) {
        toast.error(`Row ${i + 1}: Performance Fee is required.`);
        return;
      }

      if (!isValidPercentage(row.performance_fee)) {
        toast.error(`Row ${i + 1}: Invalid Performance Fee % (0–100).`);
        return;
      }

      if (
        row.side_letter_management_fee &&
        !isValidPercentage(row.side_letter_management_fee)
      ) {
        toast.error(`Row ${i + 1}: Invalid Side Letter Mgmt Fee % (0–100).`);
        return;
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Fix validation errors before saving.");
      return;
    }

    // --- Prepare Payload ---
    const endpoint = editFund ? "/product/updateProducts" : "/product/saveProductDetails";

    const selectedFundObj = activeFunds.find(
      (f) => String(f.fund_name).trim().toUpperCase() === normalizedFund
    );

    const resolvedFundName = selectedFundObj
      ? selectedFundObj.fund_name
      : selectedFundName;
    const resolvedFundCode =
      fund_code || (selectedFundObj ? selectedFundObj.fund_code : null);
    const resolvedProductType = selectedProductType || "";

    // Find the matching row in grid to get updated values
    const matchedRow = classGridData.find(
      (r) => (r.class_code || "").trim().toUpperCase() === (trimmedClassCode || "").toUpperCase()
    );

    const payload = {
      fundName: resolvedFundName || "",
      fundCode: resolvedFundCode || "",
      productType: resolvedProductType,
      classCode: trimmedClassCode ? trimmedClassCode.toUpperCase() : null,
      managementFee:
        matchedRow && matchedRow.management_fee !== "" && matchedRow.management_fee !== null
          ? parseFloat(matchedRow.management_fee)
          : null,
      sideletterFee:
        matchedRow && matchedRow.side_letter_management_fee !== "" &&
          matchedRow.side_letter_management_fee !== null
          ? parseFloat(matchedRow.side_letter_management_fee)
          : null,
      performanceFee:
        matchedRow && matchedRow.performance_fee !== "" &&
          matchedRow.performance_fee !== null
          ? parseFloat(matchedRow.performance_fee)
          : null,
      distributorHide: !!hide_from_all_distributors,

      classDetails: classGridData.map((c) => ({
        productId: selectedFundProductId || null, // 🆕 include inside each class
        fundId: selectedFundId || null,
        productTypeId: selectedProductTypeId || null,
        fundName: resolvedFundName || "",
        fundCode: resolvedFundCode || "",
        productType: resolvedProductType,
        distributorHide: !!hide_from_all_distributors,
        classCode: (c.class_code || "").trim().toUpperCase(),
        managementFee:
          c.management_fee?.toString().trim() === ""
            ? null
            : parseFloat(c.management_fee),
        sideletterFee:
          c.side_letter_management_fee?.toString().trim() === ""
            ? null
            : parseFloat(c.side_letter_management_fee),
        performanceFee:
          c.performance_fee?.toString().trim() === ""
            ? null
            : parseFloat(c.performance_fee),
        size:
          c.size?.toString().trim() === ""
            ? null
            : c.size,
      })),
    };

    console.log("📤 Final Payload:", payload);


    try {
      const res = await post(endpoint, payload);
      if (res.data.Success) {
        toast.success(res.data.Message || "Product details saved successfully!");
        setDialogVisible(false);
        resetForm();
        fetchProducts();
      } else {
        toast.error(res.data.Message || "Error saving product details.");
      }
    } catch (err) {
      console.error("API Error:", err);
      toast.error("Submission failed. Check console for details.");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        accessor: "action",
        size: 100,
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg transition-all"
              onClick={() => handleOpenDialog(row.original)}
            >
              <SquarePen size={18} />
            </Button>
          </div>
        ),
        disableSortBy: true,
      },
      {
        Header: "Product",
        accessor: "product",
        size: 150,
        Cell: ({ value }) => <span className="font-normal text-gray-900">{value}</span>
      },
      {
        Header: "Product Type",
        accessor: "product_type",
        size: 180,
        Cell: ({ value }) => <span className="text-gray-700 font-normal">{value}</span>
      },
      {
        Header: "Fund Name",
        accessor: "fund_name",
        size: 200,
        Cell: ({ value }) => (
          <div className="font-normal text-sm text-gray-800 uppercase leading-tight max-w-[180px] whitespace-normal line-clamp-2 break-words mx-auto" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Fund Code",
        accessor: "fund_code",
        size: 150,
        Cell: ({ value }) => <span className="text-gray-800 font-normal text-xs">{value}</span>
      },
      {
        Header: "Class Code",
        accessor: "class_code",
        size: 150,
        Cell: ({ value }) => <span className="text-gray-800 font-normal uppercase">{value}</span>
      },
      {
        Header: "Mgmt Fee %",
        accessor: "management_fee_sharing",
        size: 150,
        Cell: ({ value }) => (
          <span className="font-normal text-gray-700">
            {value !== null && value !== undefined && value !== "" ? `${value}%` : "-"}
          </span>
        ),
      },
      {
        Header: "Contribution (In CR)",
        accessor: "size",
        size: 180,
        Cell: ({ value }) => (
          <span className="font-normal text-gray-800">
            {value !== null && value !== undefined && value !== "" ? `${value}` : "-"}
          </span>
        ),
      },
      {
        Header: "Perf Fee %",
        accessor: "performing_fee_sharing",
        size: 130,
        Cell: ({ value }) => (
          <span className="font-normal text-gray-700">
            {value !== null && value !== undefined && value !== "" ? `${value}%` : "-"}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "is_active",
        size: 120,
        Cell: ({ value }) => {
          const statusText = value ? "Active" : "Inactive";
          const bgColor = value ? "bg-[#EBFFF3]" : "bg-[#F3F4F6]";
          const textColor = value ? "text-[#10B981]" : "text-[#6B7280]";
          const dotColor = value ? "bg-[#10B981]" : "bg-[#9CA3AF]";

          return (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-normal w-fit mx-auto`}>
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {statusText}
            </div>
          );
        },
      },
    ],
    []
  );

  const filteredProducts = useMemo(() => {
    if (!globalSearch.trim()) return products;
    const lowerSearch = globalSearch.toLowerCase();
    return products.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(lowerSearch)
      )
    );
  }, [products, globalSearch]);

  const downloadCSV = () => {
    if (!products || products.length === 0) {
      toast("No data to download");
      return;
    }
    const sortedData = [...products].sort((a, b) => Number(a.id) - Number(b.id));
    const headers = Object.keys(sortedData[0]);
    const csvRows = [
      headers.join(","),
      ...sortedData.map((row) =>
        headers
          .map((field) => `"${(row[field] ?? "").toString().replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "product_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 w-full max-w-3xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by product, fund, code..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white h-[42px]"
            />
          </div>
          <button
            type="button"
            onClick={downloadCSV}
            className="flex items-center gap-2 text-gray-700 border border-gray-200 hover:bg-gray-50 bg-white h-[42px] px-4 rounded-lg font-medium shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-nowrap">Export To Excel</span>
          </button>
        </div>

        <button
          className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-sm border-none h-[42px] px-5 rounded-lg transition-colors"
          onClick={() => handleOpenDialog()}
        >
          <Plus className="w-4 h-4" />
          <span className="font-medium text-sm text-nowrap">Add Product</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500 bg-white">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            Loading products...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 px-6">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-300" />
            </div>
            <div className="text-gray-900 text-lg font-bold mb-2">
              No Results Found
            </div>
            <div className="text-gray-500 max-w-xs mx-auto">
              No products match your current search criteria. Try a different term.
            </div>
          </div>
        ) : (
          <ReusableTable columns={columns} data={filteredProducts} />
        )}
      </div>

      <CustomDialog
        visible={dialogVisible}
        onHide={() => {
          setDialogVisible(false);
          resetForm();
        }}
        header={
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">{editFund ? "Edit Product" : "Add Product"}</span>
          </div>
        }
        width="65vw"
      >
        <div className="space-y-6 pt-4">
          <div className="grid grid-cols-3 gap-6">
            {/* Fund Name Select */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                Fund Name <span className="text-red-500">*</span>
              </label>
              <Select
                value={
                  activeFunds
                    .map((fund) => ({
                      label: fund.fund_name,
                      value: fund.fund_name,
                    }))
                    .find((opt) => opt.value === formData.fund_id) || null
                }
                onChange={(selected) =>
                  setFormData({ ...formData, fund_id: selected?.value || "" })
                }
                options={activeFunds.map((fund) => ({
                  label: fund.fund_name,
                  value: fund.fund_name,
                }))}
                placeholder="Select Fund"
                isDisabled={isFundLocked || !!editFund}
                isClearable={!isFundLocked && !editFund}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: '#E5E7EB',
                    fontSize: '0.875rem',
                    height: '42px',
                    '&:hover': { borderColor: '#6366F1' },
                    boxShadow: 'none'
                  })
                }}
              />
            </div>

            {/* Fund Code Readonly */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                Fund Code
              </label>
              <div className="w-full h-[42px] px-4 rounded-lg bg-gray-50 border border-gray-200 flex items-center text-sm font-bold text-gray-600">
                {formData.fund_code || "-"}
              </div>
            </div>

            {/* Product Type Readonly */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                Product Type
              </label>
              <div className="w-full h-[42px] px-4 rounded-lg bg-gray-50 border border-gray-200 flex items-center text-sm font-bold text-gray-600">
                {mappingData.productTypes.find(
                  (pt) => String(pt.id) === String(formData.product_type_id)
                )?.product_type_code || "-"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 items-end">
            <div className="flex flex-col col-span-2 gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                Class Code <span className="text-red-500">*</span>
              </label>
              <div className="flex h-[42px]">
                <input
                  type="text"
                  placeholder="Enter Class Code (e.g. A1, B2)"
                  value={formData.class_code}
                  onChange={(e) => {
                    const input = e.target.value;
                    const isValid = /^[a-zA-Z0-9-]*$/.test(input);
                    if (isValid) {
                      setFormData({ ...formData, class_code: input });
                    }
                  }}
                  disabled={!!editFund}
                  className={`flex-1 px-4 border border-gray-200 rounded-l-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow ${editFund ? "bg-gray-50 cursor-not-allowed font-bold" : "bg-white"
                    }`}
                />
                <button
                  type="button"
                  onClick={handleAddClassCode}
                  disabled={!!editFund}
                  className={`px-6 rounded-r-lg font-bold text-sm transition-all flex items-center gap-2 ${editFund
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-[#4F46E5] text-white hover:bg-[#4338CA] active:scale-95 shadow-sm"
                    }`}
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 pb-2.5">
              <div
                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${formData.hide_from_all_distributors ? "bg-red-500" : "bg-gray-200"
                  }`}
                onClick={() => {
                  if (!editFund) {
                    setFormData({
                      ...formData,
                      hide_from_all_distributors: !formData.hide_from_all_distributors,
                    });
                  }
                }}
              >
                <div
                  className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-transform ${formData.hide_from_all_distributors ? "translate-x-6" : "translate-x-1"
                    }`}
                />
              </div>
              <label className="text-sm font-bold text-gray-700 cursor-pointer select-none">
                Don't Show to All Distributors
              </label>
            </div>
          </div>

          {/* Show table only when mandatory fields are filled AND classGridData has items */}
          {areMandatoryFieldsFilled() && classGridData.length > 0 && (
            <>
              <div className="mt-8 overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Class Code</th>
                      <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Mgmt Fee %</th>
                      <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Side Letter Mgmt %</th>
                      <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Contribution (In CR) (CR)</th>
                      <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Perf Fee %</th>
                      <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider text-[11px] text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {classGridData.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 font-bold text-gray-900 uppercase">{row.class_code}</td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={row.management_fee}
                            onChange={(e) =>
                              handleGridChange(index, "management_fee", e.target.value)
                            }
                            className="w-full h-9 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder:text-gray-400 font-medium"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={row.side_letter_management_fee}
                            onChange={(e) =>
                              handleGridChange(index, "side_letter_management_fee", e.target.value)
                            }
                            className="w-full h-9 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder:text-gray-400 font-medium"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={row.size}
                            onChange={(e) =>
                              handleGridChange(index, "size", e.target.value)
                            }
                            className="w-full h-9 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder:text-gray-400 font-medium"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={row.performance_fee}
                            onChange={(e) =>
                              handleGridChange(index, "performance_fee", e.target.value)
                            }
                            className="w-full h-9 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder:text-gray-400 font-medium"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            onClick={() => handleDeleteRow(index)}
                            className={`text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all ${editFund ? "opacity-30 cursor-not-allowed" : ""
                              }`}
                            disabled={!!editFund}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3 mt-8 pb-4 border-t border-gray-100 pt-6">
                {!editFund && (
                  <button
                    onClick={handleClearGrid}
                    className="px-5 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={handleSubmit}
                  className="px-8 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-200 active:scale-95 transition-all flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {editFund ? "Update Product" : "Save Product"}
                </button>
              </div>
            </>
          )}
        </div>
      </CustomDialog>

      <ToastContainer />
    </div >
  );
};

export default ProductMaster;